/**
 * Shooter tank particle effects — Falling Star trail and impact.
 *
 * Color palette: gold 0xffd700, pale blue 0xccddff, white 0xffffff,
 * warm amber 0xff9900, deep orange 0xff6600.
 */

import type { ParticleCore } from '../types';

/**
 * Falling Star trail — flaming ember particles spawned each frame during descent.
 * Intensity scales with fall distance (t: 0-1 = ground to max height).
 * @param x - Tank X position
 * @param y - Tank Y position
 * @param fallT - Normalized fall distance (0 = ground level, 1 = max)
 * @param fallVy - Current vertical velocity (for speed-based scaling)
 */
export function spawnFallingStarTrail(
  core: ParticleCore,
  x: number, y: number,
  fallT: number,
  fallVy: number,
): void {
  const speed = Math.abs(fallVy);
  const intensity = Math.min(1, speed / 12);

  // Golden ember trail — embers shed behind the falling tank
  const emberCount = Math.round(2 + fallT * 3);
  for (let i = 0; i < emberCount; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.5; // mostly upward
    const emberSpeed = 1 + Math.random() * 3;
    const r = Math.random();
    const color = r < 0.3 ? 0xffffff : r < 0.6 ? 0xffd700 : r < 0.8 ? 0xff9900 : 0xff6600;
    const scale = (0.15 + fallT * 0.3) * intensity;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 12,
      y + (Math.random() - 0.5) * 8,
      angle, emberSpeed,
      color,
      0.08 + Math.random() * 0.06,
      scale,
      0.04, // light gravity
      0.95,
      true, true,
      0.7 * intensity,
      2 + Math.random(), // stretch for streaky look
    );
  }

  // Central golden glow — larger as fall progresses
  if (fallT > 0.15) {
    const glowScale = 0.4 + fallT * 0.8;
    core.spawnParticle(
      x, y,
      0xffd700,
      0, 0.5,
      0.03, 0.05,
      glowScale, glowScale * 1.5,
      0, 0.85,
      true, false, // ADD, soft
      0.5 * intensity,
    );
  }

  // Speed lines at high velocity — pale streaks rushing past
  if (speed > 6) {
    const lineAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.4; // mostly upward (opposite to fall)
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 30,
      y - 10 - Math.random() * 15,
      -Math.PI / 2 + (Math.random() - 0.5) * 0.3,
      8 + Math.random() * 6,
      Math.random() < 0.4 ? 0xffffff : 0xccddff,
      0.04 + Math.random() * 0.03,
      0.1 + Math.random() * 0.1,
      0, 0.96,
      true, true,
      0.4 * intensity,
      5 + Math.random() * 3, // very stretched
    );
  }
}

/**
 * Falling Star impact detonation — meteor crash.
 * Multi-phase: shockwaves, debris fountain, impact lines, screen-filling particles.
 * @param x - Impact X
 * @param y - Impact Y
 * @param fallT - Normalized fall distance (scales everything)
 */
export function spawnFallingStarImpact(
  core: ParticleCore,
  x: number, y: number,
  fallT: number,
): void {
  const now = Date.now();
  const scale = 0.6 + fallT * 0.8; // 0.6 at ground level, 1.4 at max height

  // Phase 0: Core flash + concentric shockwaves
  // White-hot core burst
  const coreCount = Math.round(14 * scale);
  for (let i = 0; i < coreCount; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 12,
      y + (Math.random() - 0.5) * 8,
      Math.random() < 0.4 ? 0xffffff : 0xffd700,
      2, 8 * scale,
      0.08, 0.16,
      0.8 * scale, 2.0 * scale,
      0, 0.88,
      true, true,
      0.95,
    );
  }

  // Concentric shockwaves — gold outer, white inner
  core.spawnShockwave(x, y, 0xffd700, 60 * scale, 300, 3, 0.18);
  core.spawnShockwave(x, y, 0xffffff, 35 * scale, 250, 2, 0.1);

  // Radial ring burst — 12 gold particles at even angles
  const ringCount = Math.round(12 * scale);
  for (let i = 0; i < ringCount; i++) {
    const angle = (i / ringCount) * Math.PI * 2;
    const r = Math.random();
    const color = r < 0.3 ? 0xffffff : r < 0.6 ? 0xffd700 : 0xff9900;
    core.spawnParticleAtAngle(
      x, y,
      angle, 7 + Math.random() * 5 * scale,
      color,
      0.1 + Math.random() * 0.08,
      0.4 + Math.random() * 0.3,
      0, 0.93,
      true, true,
      0.85,
      2, // stretch
    );
  }

  // Impact lines — thick radial streaks
  const lineCount = Math.round(8 * scale);
  for (let i = 0; i < lineCount; i++) {
    const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const life = 0.1 + Math.random() * 0.05;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 4,
      outerRadius: 25 + Math.random() * 15,
      expandSpeed: 220 + Math.random() * 80,
      color: Math.random() < 0.3 ? 0xffffff : 0xffd700,
      lineWidth: 2.5 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }

  // Phase 1: Debris fountain (t=60ms) — rocks and embers shooting upward
  core.pendingBlasts.push({
    timestamp: now + 60,
    callback: () => {
      // Upward debris spray
      const debrisCount = Math.round(16 * scale);
      for (let i = 0; i < debrisCount; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.7;
        const speed = 4 + Math.random() * 8 * scale;
        const r = Math.random();
        const color = r < 0.25 ? 0xffd700 : r < 0.5 ? 0xff9900 : r < 0.75 ? 0xff6600 : 0x665533;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 12,
          y,
          angle, speed,
          color,
          0.25 + Math.random() * 0.2,
          0.2 + Math.random() * 0.15,
          0.12, // gravity — arcs back down
          0.97,
          r < 0.5, true,
          0.7,
        );
      }

      // Ground-level dust burst — darker particles spreading horizontally
      for (let i = 0; i < 8; i++) {
        const angle = Math.PI + (Math.random() - 0.5) * Math.PI * 0.3;
        const side = Math.random() < 0.5 ? 0 : Math.PI; // left or right
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 15,
          y,
          side + (Math.random() - 0.5) * 0.4,
          2 + Math.random() * 4,
          Math.random() < 0.4 ? 0x554433 : 0x443322,
          0.2 + Math.random() * 0.15,
          0.5 + Math.random() * 0.4,
          0.04, 0.96,
          false, false,
          0.5,
        );
      }
    },
  });

  // Phase 2: Lingering embers (t=150ms)
  if (fallT > 0.3) {
    core.pendingBlasts.push({
      timestamp: now + 150,
      callback: () => {
        for (let i = 0; i < 6; i++) {
          const delay2 = Math.random() * 200;
          const sx = x + (Math.random() - 0.5) * 60 * scale;
          const sy = y + (Math.random() - 0.5) * 30;
          core.pendingBlasts.push({
            timestamp: now + 150 + delay2,
            callback: () => {
              core.spawnParticle(
                sx, sy,
                Math.random() < 0.5 ? 0xffd700 : 0xff9900,
                0, 0.3,
                0.06, 0.12,
                0.2, 0.4,
                -0.01, 0.92,
                true, true,
                0.7,
              );
            },
          });
        }
      },
    });
  }
}
